import PropTypes from 'prop-types';
import { Component, createRef, forwardRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { exponentialBackoff } from '../../common/utils';
import { ERROR_EMBED_NOT_READY, ERROR_INVALID_CALLBACK_PROVIDED, ERROR_UNABLE_TO_SYNC } from '../messaging/Errors';
import { createAppData, createEmbeddedPropsChangeMessage, createHostContext, createSyncMessage, isReadyMessage, isToIFrameHost, validMessage } from '../messaging/IFrameControlMessage';
import { isMalformedMessageEvent, isMessageToMe } from '../messaging/IFrameMessage';
import { isNotNull } from '../utils';
import { getIFrameSrc, getOrigin } from '../utils/IFrameSrc';
import { logHostError, logHostMessageReceived, logHostMessageSent } from '../utils/Logger';
import RefType from '../utils/RefType';
import UUID from '../utils/UUID';
import { jsx as _jsx } from "react/jsx-runtime";
export class UIIFrame extends Component {
  constructor(props) {
    super(props);
    this.callbacks = {};
    this.handleReadyTimer = null;
    this.abortSync = null;
    this.onLoad = () => {
      var _this$iFrame$ref$curr;
      this.iFrame.loaded = true;
      if (typeof ((_this$iFrame$ref$curr = this.iFrame.ref.current) === null || _this$iFrame$ref$curr === void 0 ? void 0 : _this$iFrame$ref$curr.removeEventListener) === 'function') {
        var _this$iFrame$ref$curr2;
        (_this$iFrame$ref$curr2 = this.iFrame.ref.current) === null || _this$iFrame$ref$curr2 === void 0 || _this$iFrame$ref$curr2.removeEventListener('load', this.onLoad);
      }
    };
    this.hasCallback = message => {
      return !!this.callbacks[message.id];
    };
    this.validCallback = callback => {
      if (typeof callback === 'function') {
        return true;
      }
      logHostError(this.props.name, ERROR_INVALID_CALLBACK_PROVIDED, JSON.stringify(callback));
      return false;
    };
    this.isFromEmbed = message => {
      return message.from === this.iFrame.uuid;
    };
    this.isToEmbed = message => {
      return isMessageToMe(message, this.iFrame.uuid, this.props.group);
    };
    this.dispatchCallback = message => {
      this.callbacks[message.id](message.payload);
      delete this.callbacks[message.id];
    };
    this._simulatePostMessage = event => {
      this.handleReceiveMessage(event);
    };
    this.sendMessage = (message, callback) => {
      if (!this.iFrame.ref || !this.iFrame.ref.current || !this.iFrame.ref.current.contentWindow) {
        return;
      }
      if (callback && this.validCallback(callback)) {
        this.callbacks[message.id] = callback;
      }
      if (!this.iFrame.loaded) {
        return;
      }
      this.postMessage.call(this, message, this.iFrame.origin);
      logHostMessageSent(this.props.name, message);
    };
    this.handleEmbeddedPassthruPropsChange = embeddedProps => {
      const embeddedPropsChangeMessage = createEmbeddedPropsChangeMessage(this.iFrame.uuid, embeddedProps);
      this.sendMessage(embeddedPropsChangeMessage);
    };
    this.handleReceiveMessage = event => {
      if (isMalformedMessageEvent(event)) {
        return;
      }
      if (!validMessage(event, this.iFrame.origin)) {
        return;
      }
      const {
        data: message
      } = event;
      logHostMessageReceived(this.props.name, message);
      if (this.hasCallback(message)) {
        this.dispatchCallback(message);
      } else if (this.isFromEmbed(message)) {
        if (isReadyMessage(message)) {
          this.handleIFrameReady(message);
        } else if (isToIFrameHost(message)) {
          this.props.onMessage(message);
        }
      } else if (this.isToEmbed(message)) {
        this.sendMessage(message);
      }
    };
    this.handleIFrameReady = message => {
      this.iFrame.ready = true;
      this.props.onReady(createHostContext(this.iFrame.uuid, this.sendMessage), message.payload);
    };
    this.handleInitTimeout = () => {
      const {
        name,
        onInitError
      } = this.props;
      logHostError(name, ERROR_UNABLE_TO_SYNC);
      onInitError(ERROR_UNABLE_TO_SYNC);
    };
    this.handleReadyTimeout = () => {
      this.handleReadyTimer = null;
      if (this.iFrame.ready || !this.iFrame.ref || !this.iFrame.ref.current) {
        return;
      }
      const {
        name,
        onReadyError
      } = this.props;
      logHostError(name, ERROR_EMBED_NOT_READY);
      onReadyError(ERROR_EMBED_NOT_READY);
    };
    this.handleSyncAckMessage = () => {
      this.iFrame.synced = true;
      this.handleReadyTimer = setTimeout(this.handleReadyTimeout, this.props.readyTimeout);
    };
    this.sendSync = () => {
      if (this.iFrame.synced || !this.iFrame.ref || !this.iFrame.ref.current) {
        return;
      }
      const {
        appInfo,
        appName,
        embeddedPassthruProps,
        group,
        name
      } = this.props;
      const appData = createAppData(appInfo, appName);
      const syncMessage = createSyncMessage({
        embedId: this.iFrame.uuid,
        group,
        name,
        appData,
        embeddedProps: embeddedPassthruProps
      });
      const sendSyncMessage = () => new Promise(resolve => {
        this.sendMessage(syncMessage, () => {
          resolve(true);
          this.handleSyncAckMessage();
        });
      });
      const [sendSyncMsgPromise, cleanup] = exponentialBackoff(sendSyncMessage, {
        name: this.iFrame.ref.current.name,
        timeout: this.props.initTimeout,
        initialDelay: UIIFrame.syncRetryInterval,
        maxDelay: 10000,
        enableLogging: false
      });
      this.abortSync = cleanup;
      sendSyncMsgPromise.catch(this.handleInitTimeout);
    };
    this.clearPendingTimers = () => {
      const timers = [this.handleReadyTimer];
      timers.filter(isNotNull).forEach(clearTimeout);
    };
    const iFrameSrc = getIFrameSrc(props.name, props.src);
    this.postMessage = this.postMessage.bind(this);
    this.iFrame = {
      origin: getOrigin(iFrameSrc),
      src: iFrameSrc,
      ref: props.innerRef || /*#__PURE__*/createRef(),
      synced: false,
      ready: false,
      uuid: UUID(),
      loaded: 'jasmine' in window // default to false unless in tests
    };
  }
  componentDidMount() {
    var _this$iFrame$ref$curr3;
    this.props._onTestContextReady({
      instance: this,
      iFrame: this.iFrame,
      simulatePostMessage: this._simulatePostMessage
    });
    window.addEventListener('message', this.handleReceiveMessage, false);
    if (typeof ((_this$iFrame$ref$curr3 = this.iFrame.ref.current) === null || _this$iFrame$ref$curr3 === void 0 ? void 0 : _this$iFrame$ref$curr3.addEventListener) === 'function') {
      var _this$iFrame$ref$curr4;
      (_this$iFrame$ref$curr4 = this.iFrame.ref.current) === null || _this$iFrame$ref$curr4 === void 0 || _this$iFrame$ref$curr4.addEventListener('load', this.onLoad);
    }
    this.sendSync();
  }
  shouldComponentUpdate(nextProps) {
    if (this.props.embeddedPassthruProps !== nextProps.embeddedPassthruProps) {
      this.handleEmbeddedPassthruPropsChange(nextProps.embeddedPassthruProps);
    }
    return this.props.className !== nextProps.className || this.props.height !== nextProps.height || this.props.iframePassthruProps !== nextProps.iframePassthruProps || this.props.scrolling !== nextProps.scrolling || this.props.src !== nextProps.src || this.props.width !== nextProps.width;
  }
  componentWillUnmount() {
    var _this$abortSync;
    window.removeEventListener('message', this.handleReceiveMessage);
    (_this$abortSync = this.abortSync) === null || _this$abortSync === void 0 || _this$abortSync.call(this);
    this.clearPendingTimers();
  }

  // used for testing only

  postMessage(message, originOrOptions, transfer) {
    if (!this.iFrame.ref || !this.iFrame.ref.current || !this.iFrame.ref.current.contentWindow) {
      return;
    }
    const ctx = this.iFrame.ref.current.contentWindow;
    ctx.postMessage(message, originOrOptions, transfer);
  }
  render() {
    const {
      className,
      iframePassthruProps,
      scrolling,
      height,
      width
    } = this.props;
    return (
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/iframe-has-title
      _jsx("iframe", Object.assign({
        frameBorder: 0
      }, iframePassthruProps, {
        className: className,
        ref: this.iFrame.ref,
        scrolling: scrolling ? 'yes' : 'no',
        src: this.iFrame.src,
        height: String((iframePassthruProps === null || iframePassthruProps === void 0 ? void 0 : iframePassthruProps.height) || height),
        width: String((iframePassthruProps === null || iframePassthruProps === void 0 ? void 0 : iframePassthruProps.width) || width)
      }))
    );
  }
}
UIIFrame.propTypes = {
  appInfo: PropTypes.object.isRequired,
  appName: PropTypes.string.isRequired,
  className: PropTypes.string,
  embeddedPassthruProps: PropTypes.object,
  group: PropTypes.string.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  iframePassthruProps: PropTypes.object,
  initTimeout: PropTypes.number.isRequired,
  innerRef: RefType,
  name: PropTypes.string.isRequired,
  onInitError: PropTypes.func.isRequired,
  onMessage: PropTypes.func.isRequired,
  onReady: PropTypes.func.isRequired,
  onReadyError: PropTypes.func.isRequired,
  readyTimeout: PropTypes.number.isRequired,
  scrolling: PropTypes.bool.isRequired,
  src: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  _onTestContextReady: PropTypes.func.isRequired
};
UIIFrame.defaultProps = {
  appInfo: {},
  appName: '',
  className: 'flex-grow-1',
  embeddedPassthruProps: {},
  group: 'iframeable',
  iframePassthruProps: {},
  initTimeout: 8000,
  name: 'embed',
  onInitError: emptyFunction,
  onMessage: emptyFunction,
  onReadyError: emptyFunction,
  readyTimeout: 3000,
  scrolling: true,
  height: 200,
  width: 200,
  _onTestContextReady: emptyFunction
};
UIIFrame.syncRetryInterval = 50;
export const UIIFrameWithRef = /*#__PURE__*/forwardRef((props, ref) => {
  if (typeof ref === 'function') {
    throw new Error('Callback refs are not supported yet.');
  }
  return /*#__PURE__*/_jsx(UIIFrame, Object.assign({}, props, {
    innerRef: ref
  }));
});